/**
 * @fileoverview Text Editor Service
 * 
 * Service for handling text file operations (save, load, etc.)
 */

/**
 * Text Editor Service
 */
class TextEditorService {
  
  /**
   * Save text file
   * 
   * @param {Object} saveData - Data to save
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} Save result
   */
  async saveTextFile(saveData, endpoint) {
    try {
      // Prepare the request payload
      const payload = {
        filename: `${saveData.filename}.txt`,
        content: saveData.content,
        type: 'text',
        mode: saveData.mode,
        metadata: {
          ...saveData.metadata,
          createdBy: 'text-editor-module',
          contentType: 'text/plain'
        }
      };

      // Add parrot context if provided
      if (saveData.parrotId) {
        payload.parrotId = saveData.parrotId;
      }

      // Make API request
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        file: result.file,
        message: result.message || 'File saved successfully'
      };

    } catch (error) {
      console.error('Text file save error:', error);
      throw new Error(`Failed to save text file: ${error.message}`);
    }
  }

  /**
   * Load text file
   * 
   * @param {string} fileId - File ID to load
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} Load result
   */
  async loadTextFile(fileId, endpoint) {
    try {
      const response = await fetch(`${endpoint}/${fileId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        file: result.file,
        content: result.content || '',
        metadata: result.metadata || {}
      };

    } catch (error) {
      console.error('Text file load error:', error);
      throw new Error(`Failed to load text file: ${error.message}`);
    }
  }

  /**
   * Update existing text file
   * 
   * @param {string} fileId - File ID to update
   * @param {Object} updateData - Data to update
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} Update result
   */
  async updateTextFile(fileId, updateData, endpoint) {
    try {
      const payload = {
        content: updateData.content,
        metadata: {
          ...updateData.metadata,
          updatedBy: 'text-editor-module',
          lastModified: new Date().toISOString()
        }
      };

      // Add filename if it's being updated
      if (updateData.filename) {
        payload.filename = `${updateData.filename}.txt`;
      }

      const response = await fetch(`${endpoint}/${fileId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        file: result.file,
        message: result.message || 'File updated successfully'
      };

    } catch (error) {
      console.error('Text file update error:', error);
      throw new Error(`Failed to update text file: ${error.message}`);
    }
  }

  /**
   * Delete text file
   * 
   * @param {string} fileId - File ID to delete
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} Delete result
   */
  async deleteTextFile(fileId, endpoint) {
    try {
      const response = await fetch(`${endpoint}/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        message: result.message || 'File deleted successfully'
      };

    } catch (error) {
      console.error('Text file delete error:', error);
      throw new Error(`Failed to delete text file: ${error.message}`);
    }
  }

  /**
   * Validate text file content
   * 
   * @param {string} content - Content to validate
   * @param {Object} options - Validation options
   * @returns {Object} Validation result
   */
  validateContent(content, options = {}) {
    const {
      maxLength = 50000,
      minLength = 0,
      allowEmpty = true
    } = options;

    const errors = [];
    const warnings = [];

    // Check content type
    if (typeof content !== 'string') {
      errors.push('Content must be a string');
      return { valid: false, errors, warnings };
    }

    // Check length constraints
    if (content.length < minLength) {
      errors.push(`Content must be at least ${minLength} characters long`);
    }

    if (content.length > maxLength) {
      errors.push(`Content exceeds maximum length of ${maxLength} characters`);
    }

    // Check empty content
    if (!allowEmpty && content.trim().length === 0) {
      errors.push('Content cannot be empty');
    }

    // Warnings
    if (content.length > maxLength * 0.9) {
      warnings.push(`Content is approaching the maximum length limit (${content.length}/${maxLength} characters)`);
    }

    if (content.trim().length === 0 && content.length > 0) {
      warnings.push('Content appears to contain only whitespace');
    }

    // Check for potentially problematic characters
    const controlChars = content.match(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g);
    if (controlChars) {
      warnings.push('Content contains control characters that may cause display issues');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      stats: {
        length: content.length,
        lines: content.split('\n').length,
        words: content.trim().split(/\s+/).filter(word => word.length > 0).length
      }
    };
  }

  /**
   * Auto-save content to local storage
   * 
   * @param {string} key - Storage key
   * @param {Object} data - Data to save
   * @returns {boolean} Success status
   */
  autoSave(key, data) {
    try {
      const autoSaveData = {
        ...data,
        timestamp: Date.now(),
        version: '1.0'
      };

      localStorage.setItem(key, JSON.stringify(autoSaveData));
      return true;
    } catch (error) {
      console.warn('Auto-save failed:', error);
      return false;
    }
  }

  /**
   * Load auto-saved content from local storage
   * 
   * @param {string} key - Storage key
   * @returns {Object|null} Auto-saved data or null
   */
  loadAutoSave(key) {
    try {
      const data = localStorage.getItem(key);
      if (!data) return null;

      const parsed = JSON.parse(data);
      
      // Check if auto-save is recent (within 24 hours)
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      if (Date.now() - parsed.timestamp > maxAge) {
        localStorage.removeItem(key);
        return null;
      }

      return parsed;
    } catch (error) {
      console.warn('Failed to load auto-save:', error);
      return null;
    }
  }

  /**
   * Clear auto-saved content
   * 
   * @param {string} key - Storage key
   * @returns {boolean} Success status
   */
  clearAutoSave(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('Failed to clear auto-save:', error);
      return false;
    }
  }

  /**
   * Get all auto-save keys for cleanup
   * 
   * @returns {string[]} Array of auto-save keys
   */
  getAutoSaveKeys() {
    const keys = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('text-editor-autosave-')) {
          keys.push(key);
        }
      }
    } catch (error) {
      console.warn('Failed to get auto-save keys:', error);
    }
    return keys;
  }

  /**
   * Clean up old auto-save data
   * 
   * @param {number} maxAge - Maximum age in milliseconds (default: 7 days)
   * @returns {number} Number of items cleaned up
   */
  cleanupAutoSave(maxAge = 7 * 24 * 60 * 60 * 1000) {
    let cleaned = 0;
    const keys = this.getAutoSaveKeys();

    keys.forEach(key => {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);
          if (Date.now() - parsed.timestamp > maxAge) {
            localStorage.removeItem(key);
            cleaned++;
          }
        }
      } catch (error) {
        // Remove corrupted auto-save data
        localStorage.removeItem(key);
        cleaned++;
      }
    });

    return cleaned;
  }
}

// Export singleton instance
export const textEditorService = new TextEditorService();